import { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import {
  getAvailability, saveAvailability,
  GetAvailabilityOutputType,
} from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Calendar, Clock, Plus, Trash2, Save, Loader2, Users, User,
  RefreshCw, AlertCircle,
} from 'lucide-react';
import { isManagerPlus } from '../lib/roles';

type Schedule = GetAvailabilityOutputType['schedules'][0];
type TeamMember = GetAvailabilityOutputType['teamMembers'][0];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TYPES = ['Available', 'Preferred', 'Unavailable', 'Flexible'];
const TYPE_COLORS: Record<string, string> = {
  Available: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  Preferred: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  Unavailable: 'bg-destructive/15 text-destructive border-destructive/30',
  Flexible: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
};
const DAY_SHORT: Record<string, string> = {
  Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu',
  Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun',
};

function DayColumn({ day, entries, onAdd, onRemove }: {
  day: string;
  entries: Schedule[];
  onAdd: (day: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden flex flex-col">
      <div className="px-3 py-2 border-b border-border bg-muted/30 text-center">
        <p className="text-xs font-semibold text-foreground">{DAY_SHORT[day] || day}</p>
      </div>
      <div className="flex-1 p-2 space-y-1.5 min-h-[80px]">
        {entries.length === 0 && (
          <p className="text-[10px] text-muted-foreground text-center py-4">No slots</p>
        )}
        {entries.map(e => (
          <div key={e.id} className={`rounded px-2 py-1.5 border text-[11px] ${TYPE_COLORS[e.availabilityType] || 'bg-muted text-muted-foreground border-border'}`}>
            <div className="flex items-center justify-between gap-1">
              <span className="font-medium">{e.startTime}–{e.endTime}</span>
              <button onClick={() => onRemove(e.id)} className="opacity-50 hover:opacity-100">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            <p className="text-[10px] mt-0.5 opacity-80">{e.availabilityType}</p>
            {e.notes && <p className="text-[10px] mt-0.5 italic opacity-70 truncate">{e.notes}</p>}
          </div>
        ))}
      </div>
      <button
        onClick={() => onAdd(day)}
        className="border-t border-border px-3 py-1.5 text-[11px] text-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-1"
      >
        <Plus className="w-3 h-3" /> Add
      </button>
    </div>
  );
}

function TeamGrid({ schedules, members, filter }: {
  schedules: Schedule[];
  members: TeamMember[];
  filter: string;
}) {
  const filtered = filter === 'all' ? members : members.filter(m => m.department === filter);
  const departments = [...new Set(members.map(m => m.department).filter(Boolean))];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={filter} onValueChange={() => {}}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{filtered.length} staff</p>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[700px]">
          {/* Header */}
          <div className="grid grid-cols-8 gap-1 mb-1">
            <div className="text-xs font-semibold text-muted-foreground px-2 py-1">Staff</div>
            {DAYS.map(d => (
              <div key={d} className="text-xs font-semibold text-muted-foreground text-center py-1">{DAY_SHORT[d]}</div>
            ))}
          </div>

          {/* Rows */}
          {filtered.map(member => {
            const memberSchedules = schedules.filter(s => s.employeeId === member.id);
            return (
              <div key={member.id} className="grid grid-cols-8 gap-1 mb-1">
                <div className="text-xs text-foreground px-2 py-2 truncate border border-border rounded bg-card">
                  {member.name}
                </div>
                {DAYS.map(day => {
                  const dayEntries = memberSchedules.filter(s => s.dayOfWeek === day);
                  if (dayEntries.length === 0) {
                    return <div key={day} className="border border-border rounded bg-muted/20 py-2 text-center text-[10px] text-muted-foreground">—</div>;
                  }
                  const primary = dayEntries[0];
                  return (
                    <div key={day} className={`border rounded py-1 px-1 text-center ${TYPE_COLORS[primary.availabilityType] || ''}`}>
                      <p className="text-[10px] font-medium">{primary.startTime}–{primary.endTime}</p>
                      <p className="text-[9px] opacity-80">{primary.availabilityType}</p>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-40" />
              No staff have set availability yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AvailabilityCalendar() {
  const { user } = useAuth();
  const [data, setData] = useState<GetAvailabilityOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addDay, setAddDay] = useState<string | null>(null);
  const [deleteIds, setDeleteIds] = useState<string[]>([]);
  const [newEntries, setNewEntries] = useState<Array<{
    tempId: string; dayOfWeek: string; startTime: string; endTime: string;
    availabilityType: string; notes: string;
  }>>([]);
  const [deptFilter, setDeptFilter] = useState('all');
  const [addForm, setAddForm] = useState({ startTime: '09:00', endTime: '17:00', type: 'Available', notes: '' });

  const isManager = isManagerPlus(user?.role);

  const loadData = useCallback(async (scope?: 'own' | 'team') => {
    setLoading(true);
    try {
      const d = await getAvailability({ scope });
      setData(d);
    } catch { toast.error('Failed to load availability'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  const mySchedules = useMemo(() => {
    if (!data) return [];
    const myId = user?.id || '';
    return [
      ...data.schedules.filter(s => s.employeeId === myId && !deleteIds.includes(s.id)),
      ...newEntries.map(e => ({ ...e, id: e.tempId, employeeId: myId, employeeName: '', active: true })),
    ];
  }, [data, user, deleteIds, newEntries]);

  const handleAddSlot = () => {
    if (!addDay) return;
    setNewEntries(prev => [...prev, {
      tempId: `new-${Date.now()}-${Math.random()}`,
      dayOfWeek: addDay,
      startTime: addForm.startTime,
      endTime: addForm.endTime,
      availabilityType: addForm.type,
      notes: addForm.notes,
    }]);
    setAddDay(null);
    setAddForm({ startTime: '09:00', endTime: '17:00', type: 'Available', notes: '' });
  };

  const handleRemove = (id: string) => {
    if (id.startsWith('new-')) {
      setNewEntries(prev => prev.filter(e => e.tempId !== id));
    } else {
      setDeleteIds(prev => [...prev, id]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAvailability({
        entries: newEntries.map(e => ({
          dayOfWeek: e.dayOfWeek,
          startTime: e.startTime,
          endTime: e.endTime,
          availabilityType: e.availabilityType,
          notes: e.notes,
        })),
        deleteIds,
      });
      toast.success('Availability saved');
      setNewEntries([]);
      setDeleteIds([]);
      await loadData();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const hasChanges = newEntries.length > 0 || deleteIds.length > 0;

  if (loading) return (
    <div className="px-4 md:px-8 py-6 max-w-5xl mx-auto space-y-4">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-7 gap-2">{[...Array(7)].map((_, i) => <Skeleton key={i} className="h-40" />)}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-4 md:px-8 py-5">
        <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" /> Availability Calendar
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">Set your weekly availability pattern</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => loadData()} className="gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            {hasChanges && (
              <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save Changes
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 py-6 max-w-5xl mx-auto">
        {isManager ? (
          <Tabs defaultValue="my" className="space-y-4">
            <TabsList>
              <TabsTrigger value="my" className="gap-1.5"><User className="w-3.5 h-3.5" /> My Availability</TabsTrigger>
              <TabsTrigger value="team" className="gap-1.5" onClick={() => loadData('team')}>
                <Users className="w-3.5 h-3.5" /> Team View
              </TabsTrigger>
            </TabsList>

            <TabsContent value="my">
              <MyWeekGrid schedules={mySchedules} onAdd={setAddDay} onRemove={handleRemove} />
              {hasChanges && (
                <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20 flex items-center justify-between">
                  <p className="text-sm text-primary">
                    {newEntries.length > 0 && `${newEntries.length} new slot(s)`}
                    {newEntries.length > 0 && deleteIds.length > 0 && ' · '}
                    {deleteIds.length > 0 && `${deleteIds.length} removed`}
                  </p>
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                    Save
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="team">
              <TeamGrid schedules={data?.schedules || []} members={data?.teamMembers || []} filter={deptFilter} />
            </TabsContent>
          </Tabs>
        ) : (
          <>
            <MyWeekGrid schedules={mySchedules} onAdd={setAddDay} onRemove={handleRemove} />
            {hasChanges && (
              <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20 flex items-center justify-between">
                <p className="text-sm text-primary">
                  {newEntries.length > 0 && `${newEntries.length} new slot(s)`}
                  {newEntries.length > 0 && deleteIds.length > 0 && ' · '}
                  {deleteIds.length > 0 && `${deleteIds.length} removed`}
                </p>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                  Save
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add slot dialog */}
      <Dialog open={!!addDay} onOpenChange={v => !v && setAddDay(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" /> Add Availability — {addDay}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Start Time</label>
                <Input type="time" value={addForm.startTime} onChange={e => setAddForm(f => ({ ...f, startTime: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">End Time</label>
                <Input type="time" value={addForm.endTime} onChange={e => setAddForm(f => ({ ...f, endTime: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Type</label>
              <Select value={addForm.type} onValueChange={v => setAddForm(f => ({ ...f, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Notes (optional)</label>
              <Textarea
                value={addForm.notes}
                onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. Can do evening only"
                className="min-h-16"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setAddDay(null)}>Cancel</Button>
              <Button className="flex-1" onClick={handleAddSlot}>
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Slot
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MyWeekGrid({ schedules, onAdd, onRemove }: {
  schedules: Schedule[];
  onAdd: (day: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
      {DAYS.map(day => (
        <DayColumn
          key={day}
          day={day}
          entries={schedules.filter(s => s.dayOfWeek === day)}
          onAdd={onAdd}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
}
